import { CART_SOURCE, type CartPartial } from './cart-partial.js';
import { parseItemQuantity } from './parse-money.js';

/** Site-neutral header / app-bar cart count selectors (count only). */
const HEADER_COUNT_SELECTORS = [
  '[data-cart-count]',
  '[data-cart-quantity]',
  '[data-item-count]',
  '.cart-count',
  '.cart-count-bubble',
  '.cart-quantity',
  '.mini-cart-count',
  '.header-cart-count',
  '#cart-count',
  '#CartCount',
  'cart-count-bubble',
  '.cart-link__bubble',
  '[aria-label*="cart" i] .count',
  '[aria-label*="basket" i] .count',
  '[class*="cart-count" i]',
  '[class*="CartCount" i]',
  '[id*="cart-count" i]',
  '[id*="CartCount" i]',
].join(', ');

function readCountFromElement(el: Element): number | undefined {
  const attr =
    el.getAttribute('data-cart-count') ??
    el.getAttribute('data-cart-quantity') ??
    el.getAttribute('data-item-count') ??
    el.getAttribute('data-count');
  const fromAttr = parseItemQuantity(attr);
  if (fromAttr != null) return fromAttr;

  const text = (el.textContent ?? '').trim();
  // Prefer a lone integer / "(3)" style badge text.
  const match = text.match(/^\(?\s*(\d{1,4})\s*\)?$/);
  if (match) return parseItemQuantity(match[1]);
  return undefined;
}

function isLikelyInChrome(el: Element): boolean {
  return Boolean(
    el.closest(
      'header, nav, [role="banner"], [class*="header" i], [class*="navbar" i], [class*="top-bar" i], [id*="header" i]',
    ),
  );
}

/**
 * Read cart item count from a header / app-bar badge.
 * Never invents cart value.
 */
export function collectHeaderCartCountPartial(
  doc: Document = document,
): CartPartial | null {
  const nodes = Array.from(doc.querySelectorAll(HEADER_COUNT_SELECTORS));
  if (nodes.length === 0) return null;

  const ranked = nodes
    .map((el) => {
      const count = readCountFromElement(el);
      if (count == null) return null;
      const inChrome = isLikelyInChrome(el);
      return { count, score: inChrome ? 2 : 1 };
    })
    .filter((row): row is { count: number; score: number } => row != null);

  if (ranked.length === 0) return null;

  ranked.sort((a, b) => b.score - a.score || b.count - a.count);
  const best = ranked[0];

  return {
    source: CART_SOURCE.headerBadge,
    confidence: 'low',
    itemsCount: best.count,
    ...(best.count === 0 ? { emptyExplicit: true } : {}),
  };
}
